import React, { useState } from 'react';
import ArrayEditor from './ArrayEditor';



  const AccordionSection = ({ sectionKey, title, children, toggleSection,defaultExpanded=false,expandedSections}) => {
    const isExpanded = expandedSections[sectionKey];

    return (
      <div className="border border-gray-200 rounded-lg mb-3 bg-white shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
          aria-expanded={isExpanded}
        >
          <span className="text-lg font-semibold text-gray-900">{title}</span>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-4 pt-0 space-y-4">
            {children}
          </div>
        </div>
      </div>
    );
  };















export default function EditReportForm({ report, onChange, errors = {} }) {
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    keyMetrics: false,
    insights: false,
    whatIsMarket: false,
    trends: false,
    drivers: false,
    restraints: false,
    opportunities: false,
    recentDevelopments: false,
    contentSections: false,
    regionalInsights: false,
    faqs: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFieldChange = (field, value) => {
    onChange({
      ...report,
      [field]: value
    });
  };

  const handleContentFieldChange = (field, value) => {
    onChange({
      ...report,
      content: {
        ...report.content,
        [field]: value
      }
    });
  };

  const handleFAQsChange = (faqs) => {
    onChange({
      ...report,
      faqs: faqs
    });
  };

  const handleKeyMetricsChange = (field, value) => {
    onChange({
      ...report,
      keyMetrics: {
        ...(report.keyMetrics || {}),
        [field]: value
      }
    });
  };

  const handleKeyMetricsNestedChange = (parentField, childField, value) => {
    onChange({
      ...report,
      keyMetrics: {
        ...(report.keyMetrics || {}),
        [parentField]: {
          ...(report.keyMetrics?.[parentField] || {}),
          [childField]: value
        }
      }
    });
  };

  // Helper functions for string arrays
  const convertStringArrayToObjects = (arr) => {
    return Array.isArray(arr) ? arr.map(str => ({ value: str || '' })) : [];
  };

  const convertObjectsToStringArray = (arr) => {
    return Array.isArray(arr) ? arr.map(obj => (typeof obj === 'string' ? obj : obj?.value || '')).filter(Boolean) : [];
  };



  return (
    <div className="space-y-4">
      <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="basicInfo" title="Basic Information" defaultExpanded >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={report.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Report title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Industry <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={report.industry || ''}
            onChange={(e) => handleFieldChange('industry', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.industry ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Industry name"
          />
          {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Market Name
          </label>
          <input
            type="text"
            value={report.marketName || ''}
            onChange={(e) => handleFieldChange('marketName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Market name"
          />
        </div>

        {report.template && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template
            </label>
            <input
              type="text"
              value={report.template}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>
        )}

        {report.usedModel && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Used Model
            </label>
            <input
              type="text"
              value={report.usedModel}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>
        )}
      </AccordionSection>

      <AccordionSection sectionKey="keyMetrics" title="Key Metrics">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Year
              </label>
              <input
                type="number"
                value={report.keyMetrics?.baseYear || report.baseYear || ''}
                onChange={(e) => handleKeyMetricsChange('baseYear', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 2025"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forecast Year
              </label>
              <input
                type="number"
                value={report.keyMetrics?.forecastYear || (report.baseYear ? report.baseYear + 10 : '') || ''}
                onChange={(e) => handleKeyMetricsChange('forecastYear', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 2035"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Forecast Period
            </label>
            <input
              type="text"
              value={report.keyMetrics?.forecastPeriod || ''}
              onChange={(e) => handleKeyMetricsChange('forecastPeriod', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 2026-2035"
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Market Size</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
                <input
                  type="number"
                  step="0.1"
                  value={report.keyMetrics?.marketSize?.value || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('marketSize', 'value', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 42.6"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                <input
                  type="text"
                  value={report.keyMetrics?.marketSize?.unit || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('marketSize', 'unit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Billion"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Formatted</label>
                <input
                  type="text"
                  value={report.keyMetrics?.marketSize?.formatted || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('marketSize', 'formatted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., USD 42.6 Billion"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Projected Market Size</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
                <input
                  type="number"
                  step="0.1"
                  value={report.keyMetrics?.projectedMarketSize?.value || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('projectedMarketSize', 'value', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 68.0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                <input
                  type="text"
                  value={report.keyMetrics?.projectedMarketSize?.unit || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('projectedMarketSize', 'unit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Billion"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Formatted</label>
                <input
                  type="text"
                  value={report.keyMetrics?.projectedMarketSize?.formatted || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('projectedMarketSize', 'formatted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., USD 68.0 Billion"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">CAGR</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
                <input
                  type="number"
                  step="0.1"
                  value={report.keyMetrics?.cagr?.value || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('cagr', 'value', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 6.2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Formatted</label>
                <input
                  type="text"
                  value={report.keyMetrics?.cagr?.formatted || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('cagr', 'formatted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 6.2%"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
                <input
                  type="text"
                  value={report.keyMetrics?.cagr?.period || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('cagr', 'period', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 2026-2035"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Dominant Region</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
                <input
                  type="text"
                  value={report.keyMetrics?.dominantRegion?.region || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('dominantRegion', 'region', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., North America"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Share %</label>
                <input
                  type="number"
                  step="0.1"
                  value={report.keyMetrics?.dominantRegion?.sharePercent || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('dominantRegion', 'sharePercent', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 42.5"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Formatted</label>
                <input
                  type="text"
                  value={report.keyMetrics?.dominantRegion?.formatted || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('dominantRegion', 'formatted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., North America (42.5% Share)"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Fastest Growing Region</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
                <input
                  type="text"
                  value={report.keyMetrics?.fastestGrowingRegion?.region || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('fastestGrowingRegion', 'region', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Asia-Pacific"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">CAGR %</label>
                <input
                  type="number"
                  step="0.1"
                  value={report.keyMetrics?.fastestGrowingRegion?.cagrPercent || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('fastestGrowingRegion', 'cagrPercent', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 8.5"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Formatted</label>
                <input
                  type="text"
                  value={report.keyMetrics?.fastestGrowingRegion?.formatted || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('fastestGrowingRegion', 'formatted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Asia-Pacific (8.5% CAGR)"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Leading Segment</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  value={report.keyMetrics?.leadingSegment?.name || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('leadingSegment', 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Online Payday Loans"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Share %</label>
                <input
                  type="number"
                  step="0.1"
                  value={report.keyMetrics?.leadingSegment?.sharePercent || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('leadingSegment', 'sharePercent', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 65.5"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Formatted</label>
                <input
                  type="text"
                  value={report.keyMetrics?.leadingSegment?.formatted || ''}
                  onChange={(e) => handleKeyMetricsNestedChange('leadingSegment', 'formatted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Online Payday Loans (65.5% Share)"
                />
              </div>
            </div>
          </div>
        </div>
      </AccordionSection>

      <AccordionSection sectionKey="insights" title="Key Market Insights">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key Market Insights
          </label>
          <textarea
            value={report.content?.keyMarketInsights || ''}
            onChange={(e) => handleContentFieldChange('keyMarketInsights', e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter key market insights (multiple paragraphs supported)"
          />
        </div>
      </AccordionSection>

      {(report.template === 'template2' || report.template === 'template3') && (
        <AccordionSection sectionKey="whatIsMarket" title="What is Market">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What is {report.marketName || 'Market'}
            </label>
            <textarea
              value={report.content?.whatIsMarket || ''}
              onChange={(e) => handleContentFieldChange('whatIsMarket', e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter explanation of market topic name (100 words)"
            />
          </div>
        </AccordionSection>
      )}

      <AccordionSection sectionKey="trends" title="Emerging Trends">
        <ArrayEditor
          items={report.content?.emergingTrends || []}
          onChange={(items) => handleContentFieldChange('emergingTrends', items)}
          fieldConfig={[
            { name: 'title', type: 'text', label: 'Title', placeholder: 'Enter trend title' },
            { name: 'summary', type: 'textarea', label: 'Summary', placeholder: 'Enter trend explanation', rows: 4 }
          ]}
          addButtonLabel="Add Trend"
          minItems={0}
        />
        {(report.template === 'template2' || report.template === 'template3') && (
          <p className="text-xs text-gray-500 mt-2">
            {report.template === 'template2' 
              ? 'Note: For Template 2, first 5 items should have empty summaries (bullet points), followed by 2-3 items with summaries.'
              : 'Note: For Template 3, each trend should be around 70 words.'}
          </p>
        )}
      </AccordionSection>

      <AccordionSection sectionKey="drivers" title="Market Drivers">
        <ArrayEditor
          items={report.content?.drivers || []}
          onChange={(items) => handleContentFieldChange('drivers', items)}
          fieldConfig={[
            { name: 'title', type: 'text', label: 'Title', placeholder: 'Enter driver title' },
            { name: 'summary', type: 'textarea', label: 'Summary', placeholder: 'Enter driver explanation', rows: 4 }
          ]}
          addButtonLabel="Add Driver"
          minItems={0}
        />
        {(report.template === 'template2' || report.template === 'template3') && (
          <p className="text-xs text-gray-500 mt-2">
            {report.template === 'template2' 
              ? 'Note: For Template 2, first 5 items should have empty summaries (bullet points), followed by 3 items with summaries.'
              : 'Note: For Template 3, each driver should be around 70 words.'}
          </p>
        )}
      </AccordionSection>

      <AccordionSection sectionKey="restraints" title="Market Restraints">
        <ArrayEditor
          items={report.content?.restraints || []}
          onChange={(items) => handleContentFieldChange('restraints', items)}
          fieldConfig={[
            { name: 'title', type: 'text', label: 'Title', placeholder: 'Enter restraint title' },
            { name: 'summary', type: 'textarea', label: 'Summary', placeholder: 'Enter restraint explanation', rows: 4 }
          ]}
          addButtonLabel="Add Restraint"
          minItems={0}
        />
        {report.template === 'template3' && (
          <p className="text-xs text-gray-500 mt-2">
            Note: For Template 3, each restraint should be around 80 words.
          </p>
        )}
      </AccordionSection>

      <AccordionSection sectionKey="opportunities" title="Market Opportunities">
        <ArrayEditor
          items={report.content?.opportunities || []}
          onChange={(items) => handleContentFieldChange('opportunities', items)}
          fieldConfig={[
            { name: 'title', type: 'text', label: 'Title', placeholder: 'Enter opportunity title' },
            { name: 'summary', type: 'textarea', label: 'Summary', placeholder: 'Enter opportunity explanation', rows: 4 }
          ]}
          addButtonLabel="Add Opportunity"
          minItems={0}
        />
        {report.template === 'template3' && (
          <p className="text-xs text-gray-500 mt-2">
            Note: For Template 3, each opportunity should be around 80 words.
          </p>
        )}
      </AccordionSection>

      <AccordionSection sectionKey="recentDevelopments" title="Recent Developments">
        <ArrayEditor
          items={report.content?.recentDevelopments || []}
          onChange={(items) => handleContentFieldChange('recentDevelopments', items)}
          fieldConfig={[
            { name: 'month', type: 'text', label: 'Month', placeholder: 'e.g., January' },
            { name: 'year', type: 'text', label: 'Year', placeholder: 'e.g., 2025' },
            { name: 'description', type: 'textarea', label: 'Description', placeholder: 'Enter description (2 sentences)', rows: 3 }
          ]}
          addButtonLabel="Add Recent Development"
          minItems={0}
        />
        <p className="text-xs text-gray-500 mt-2">
          Note: Template 1 requires 4 entries, Template 2 and 3 require 5 entries.
        </p>
      </AccordionSection>

      <AccordionSection sectionKey="contentSections" title="Content Sections">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Segmentation Analysis
          </label>
          <textarea
            value={report.content?.segmentationAnalysis || ''}
            onChange={(e) => handleContentFieldChange('segmentationAnalysis', e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter segmentation analysis"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Regulatory and Policy Analysis
          </label>
          <textarea
            value={report.content?.regulatoryAndPolicyAnalysis || ''}
            onChange={(e) => handleContentFieldChange('regulatoryAndPolicyAnalysis', e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter regulatory and policy analysis"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Innovation and Technology Analysis
          </label>
          <textarea
            value={report.content?.innovationAndTechnologyAnalysis || ''}
            onChange={(e) => handleContentFieldChange('innovationAndTechnologyAnalysis', e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter innovation and technology analysis"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key Players Analysis
          </label>
          <textarea
            value={report.content?.keyPlayersAnalysis || ''}
            onChange={(e) => handleContentFieldChange('keyPlayersAnalysis', e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter key players analysis"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key Players Description
          </label>
          <textarea
            value={report.content?.keyPlayersDescription || ''}
            onChange={(e) => handleContentFieldChange('keyPlayersDescription', e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter key players description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Geopolitical and Macroeconomic Factors
          </label>
          <textarea
            value={report.content?.geopoliticalAndMacroeconomicFactors || ''}
            onChange={(e) => handleContentFieldChange('geopoliticalAndMacroeconomicFactors', e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter geopolitical and macroeconomic factors"
          />
        </div>
      </AccordionSection>

      <AccordionSection sectionKey="regionalInsights" title="Regional Analysis">
        <div className="space-y-4">
          {report.template !== 'template3' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dominant Region
                </label>
                <div className="space-y-3 border border-gray-200 rounded-md p-4 bg-gray-50">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
                    <input
                      type="text"
                      value={report.content?.regionalInsights?.dominantRegion?.region || ''}
                      onChange={(e) => {
                        const regionalInsights = report.content?.regionalInsights || {};
                        handleContentFieldChange('regionalInsights', {
                          ...regionalInsights,
                          dominantRegion: {
                            ...regionalInsights.dominantRegion,
                            region: e.target.value,
                            sharePercent: regionalInsights.dominantRegion?.sharePercent || 0,
                            summary: regionalInsights.dominantRegion?.summary || ''
                          }
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., North America"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Share Percentage</label>
                    <input
                      type="number"
                      step="0.1"
                      value={report.content?.regionalInsights?.dominantRegion?.sharePercent || ''}
                      onChange={(e) => {
                        const regionalInsights = report.content?.regionalInsights || {};
                        handleContentFieldChange('regionalInsights', {
                          ...regionalInsights,
                          dominantRegion: {
                            ...regionalInsights.dominantRegion,
                            region: regionalInsights.dominantRegion?.region || '',
                            sharePercent: parseFloat(e.target.value) || 0,
                            summary: regionalInsights.dominantRegion?.summary || ''
                          }
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 38.5"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
                    <textarea
                      value={report.content?.regionalInsights?.dominantRegion?.summary || ''}
                      onChange={(e) => {
                        const regionalInsights = report.content?.regionalInsights || {};
                        handleContentFieldChange('regionalInsights', {
                          ...regionalInsights,
                          dominantRegion: {
                            ...regionalInsights.dominantRegion,
                            region: regionalInsights.dominantRegion?.region || '',
                            sharePercent: regionalInsights.dominantRegion?.sharePercent || 0,
                            summary: e.target.value
                          }
                        });
                      }}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter dominant region summary (~130 words)"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fastest Growing Region
                </label>
                <div className="space-y-3 border border-gray-200 rounded-md p-4 bg-gray-50">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
                    <input
                      type="text"
                      value={report.content?.regionalInsights?.fastestGrowingRegion?.region || ''}
                      onChange={(e) => {
                        const regionalInsights = report.content?.regionalInsights || {};
                        handleContentFieldChange('regionalInsights', {
                          ...regionalInsights,
                          fastestGrowingRegion: {
                            ...regionalInsights.fastestGrowingRegion,
                            region: e.target.value,
                            cagrPercent: regionalInsights.fastestGrowingRegion?.cagrPercent || 0,
                            summary: regionalInsights.fastestGrowingRegion?.summary || ''
                          }
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Asia Pacific"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">CAGR Percentage</label>
                    <input
                      type="number"
                      step="0.1"
                      value={report.content?.regionalInsights?.fastestGrowingRegion?.cagrPercent || ''}
                      onChange={(e) => {
                        const regionalInsights = report.content?.regionalInsights || {};
                        handleContentFieldChange('regionalInsights', {
                          ...regionalInsights,
                          fastestGrowingRegion: {
                            ...regionalInsights.fastestGrowingRegion,
                            region: regionalInsights.fastestGrowingRegion?.region || '',
                            cagrPercent: parseFloat(e.target.value) || 0,
                            summary: regionalInsights.fastestGrowingRegion?.summary || ''
                          }
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 26.1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
                    <textarea
                      value={report.content?.regionalInsights?.fastestGrowingRegion?.summary || ''}
                      onChange={(e) => {
                        const regionalInsights = report.content?.regionalInsights || {};
                        handleContentFieldChange('regionalInsights', {
                          ...regionalInsights,
                          fastestGrowingRegion: {
                            ...regionalInsights.fastestGrowingRegion,
                            region: regionalInsights.fastestGrowingRegion?.region || '',
                            cagrPercent: regionalInsights.fastestGrowingRegion?.cagrPercent || 0,
                            summary: e.target.value
                          }
                        });
                      }}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter fastest growing region summary (~130 words)"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {report.template === 'template3' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regions Analysis (5 regions)
              </label>
              <ArrayEditor
                items={convertStringArrayToObjects(report.content?.regionalInsights?.regionsAnalysis || [])}
                onChange={(items) => {
                  const regionalInsights = report.content?.regionalInsights || {};
                  handleContentFieldChange('regionalInsights', {
                    ...regionalInsights,
                    regionsAnalysis: convertObjectsToStringArray(items)
                  });
                }}
                fieldConfig={[
                  { name: 'value', type: 'textarea', label: 'Region Analysis', placeholder: 'Enter region analysis (~100 words)', rows: 4 }
                ]}
                addButtonLabel="Add Region Analysis"
                minItems={0}
              />
              <p className="text-xs text-gray-500 mt-2">
                Note: Should include 5 regions: North America, Europe, Asia Pacific, Latin America, Middle East & Africa.
              </p>
            </div>
          )}

          {(report.template === 'template2' || report.template === 'template3') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Top Countries Overview
              </label>
              <ArrayEditor
                items={convertStringArrayToObjects(report.content?.regionalInsights?.topCountriesOverview || [])}
                onChange={(items) => {
                  const regionalInsights = report.content?.regionalInsights || {};
                  handleContentFieldChange('regionalInsights', {
                    ...regionalInsights,
                    topCountriesOverview: convertObjectsToStringArray(items)
                  });
                }}
                fieldConfig={[
                  { name: 'value', type: 'textarea', label: 'Country Overview', placeholder: report.template === 'template2' ? 'Enter country overview (~70 words)' : 'Enter country overview (~50 words)', rows: 3 }
                ]}
                addButtonLabel="Add Country Overview"
                minItems={0}
              />
              <p className="text-xs text-gray-500 mt-2">
                Note: Should include 3 country overview paragraphs for top 4 countries.
              </p>
            </div>
          )}
        </div>
      </AccordionSection>

      <AccordionSection sectionKey="faqs" title="FAQs">
        <ArrayEditor
          items={report.faqs || []}
          onChange={handleFAQsChange}
          fieldConfig={[
            { name: 'question', type: 'textarea', label: 'Question', placeholder: 'Enter question', rows: 2 },
            { name: 'answer', type: 'textarea', label: 'Answer', placeholder: 'Enter answer', rows: 3 }
          ]}
          addButtonLabel="Add FAQ"
          minItems={0}
        />
      </AccordionSection>
    </div>
  );
}

